import { addDays, addHours, endOfDay, startOfDay, subDays } from 'date-fns'
import { AxiosError, HttpStatusCode } from 'axios'
import { TimeSlotBookingListStatusEnum, TransportStatusEnum } from '@apiTypes'
import type {
  CarrierBookingTransportDetailNested,
  BookingTransportDetailNested,
  BookingTransportPreviewNested,
  BookingsListParams,
  BookingVehicleNested,
  GateNested,
  TimeSlotBookingList,
  WaypointWithBooking,
} from '@apiTypes'
import type { RouteLocationNamedRaw } from 'vue-router'
import type { RouteAction } from '@/types/route-action'
import type { WaypointOrWaypointByHash } from '@/types/union-types'
import i18n from '@/plugins/i18n'
import { bookingTimelineLocation } from '@/views/time-slots/utils/booking-timeline.utils'
import { isNullOrUndefined } from '@/utils/common.utils'
import { isTransportStatusCanceled } from '@/views/transports/utils/transport.utils'
import { canCancelBooking, isTransportBookingActionable, isTransportBookingAllowed, transportCanceledBookingExplanation } from '@/views/time-slots/utils/transport-booking-policy.utils'
import { formatDateInZone, formatDateString, formatDateStringInZone, formatISODate, formatRelativeDayLabelInZone, ISO_DATE_FORMAT, stringToDate } from '@/utils/date.utils'
import { firstApiErrorMessage } from '@/utils/form.utils'
import { isBadRequestError } from '@/composables/dialogs/dialog-server-errors'

const BOOKING_CREATED_AT_FORMAT = 'dd.MM.yyyy - HH:mm'

/** Spaced on purpose: the booking confirmation table in Figma shows "21. 11. 2027 - 14:30". */
const BOOKING_SLOT_START_FORMAT = 'dd. MM. yyyy - HH:mm'

export interface BookingAddressLabelSource {
  name?: string
  street?: string | null
  city?: string | null
}

// Statuses a booking can actually reach today: create (PENDING, or APPROVED via auto-approve),
// the shipper's reject, cancel (carrier: PENDING → WITHDRAWN / APPROVED → CANCELED; shipper:
// APPROVED → CANCELED), and the derived EXPIRED. ARRIVED/COMPLETED have no producing action yet.
const FILTERABLE_BOOKING_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.APPROVED,
  TimeSlotBookingListStatusEnum.REJECTED,
  TimeSlotBookingListStatusEnum.WITHDRAWN,
  TimeSlotBookingListStatusEnum.CANCELED,
  TimeSlotBookingListStatusEnum.EXPIRED,
]

// Built per call with literal `t('...')` so `i18n:extract` can see every key.
export function getBookingStatusLabel(status: TimeSlotBookingListStatusEnum): string {
  const { t } = i18n.global

  const labels: Record<TimeSlotBookingListStatusEnum, string> = {
    [TimeSlotBookingListStatusEnum.PENDING]: t('Pending'),
    [TimeSlotBookingListStatusEnum.APPROVED]: t('Approved'),
    [TimeSlotBookingListStatusEnum.ARRIVED]: t('Arrived'),
    [TimeSlotBookingListStatusEnum.COMPLETED]: t('Completed'),
    [TimeSlotBookingListStatusEnum.REJECTED]: t('Rejected'),
    [TimeSlotBookingListStatusEnum.WITHDRAWN]: t('Withdrawn'),
    [TimeSlotBookingListStatusEnum.CANCELED]: t('Canceled'),
    [TimeSlotBookingListStatusEnum.EXPIRED]: t('Expired'),
  }

  return labels[status]
}

/** One labelled list for both the dialog's status field and the quick select. */
export function bookingStatusOptions(): { value: TimeSlotBookingListStatusEnum; label: string }[] {
  return FILTERABLE_BOOKING_STATUSES.map(status => ({ value: status, label: getBookingStatusLabel(status) }))
}

export enum BookingDatePreset {
  Next24Hours = 'next24Hours',
  Next7Days = 'next7Days',
  Last7Days = 'last7Days',
  Next30Days = 'next30Days',
  Last30Days = 'last30Days',
}

export function getBookingDatePresetRange(
  preset: BookingDatePreset | null,
  now: Date = new Date(),
): Pick<BookingsListParams, 'fromDatetime' | 'toDatetime'> {
  switch (preset) {
    case BookingDatePreset.Next24Hours:
      return { fromDatetime: now.toISOString(), toDatetime: addHours(now, 24).toISOString() }
    case BookingDatePreset.Next7Days:
      return { fromDatetime: now.toISOString(), toDatetime: addDays(now, 7).toISOString() }
    case BookingDatePreset.Last7Days:
      return { fromDatetime: subDays(now, 7).toISOString(), toDatetime: now.toISOString() }
    case BookingDatePreset.Next30Days:
      return { fromDatetime: now.toISOString(), toDatetime: addDays(now, 30).toISOString() }
    case BookingDatePreset.Last30Days:
      return { fromDatetime: subDays(now, 30).toISOString(), toDatetime: now.toISOString() }
    default:
      return {}
  }
}

/**
 * Calendar-date range to the instant pair the API filters on, in the caller's timezone. The upper
 * bound is the *end* of the picked day, or a single-day range would drop that day's bookings.
 */
export function createBookingTimeWindow(
  after: string | null | undefined,
  before: string | null | undefined,
): Pick<BookingsListParams, 'fromDatetime' | 'toDatetime'> {
  const from = stringToDate(after)
  const to = stringToDate(before)
  if (!from || !to)
    return {}

  const fromDatetime = formatISODate(startOfDay(from))
  const toDatetime = formatISODate(endOfDay(to))
  if (!fromDatetime || !toDatetime)
    return {}

  return { fromDatetime, toDatetime }
}

/**
 * Blank-able backend fields arrive as "", never null, so `||` (not `??`) is what renders the dash.
 */
export function formatBookingDash(value: string | null | undefined): string {
  return value || '-'
}

/**
 * The carrier select's option value for a directory carrier. Prefixed so free text the shipper types can
 * never collide with an id ("31" stays a typed name, "carrier:31" is the pick).
 */
export function carrierOptionKey(id: number): string {
  return `carrier:${id}`
}

export function isCarrierOptionKey(key: string): boolean {
  return /^carrier:\d+$/.test(key)
}

/**
 * Booking detail pages show "Not defined" for missing data (per AC); list cards keep the dash.
 */
export function formatBookingNotDefined(value: string | null | undefined): string {
  return value || i18n.global.t('Not defined')
}

/** Detail `carrierNote` is the transport detail's note; blank for a standalone booking. */
export function formatBookingCarrierNote(note: string | null | undefined): string {
  return formatBookingNotDefined(note?.trim())
}

/**
 * Address label for a booking card: the name when it has one, otherwise street and city.
 */
export function formatBookingAddress(address: BookingAddressLabelSource | null | undefined): string {
  if (!address)
    return ''

  // Blank-able backend fields arrive as "", never null, so `||` (not `??`) is what triggers the fallback
  return address.name || [address.street, address.city].filter(Boolean).join(', ')
}

export type BookingDeletionSource = {
  address?: { isDeleted: boolean } | null
  gate?: { isDeleted: boolean } | null
}

/**
 * Why a booking whose address or gate was deleted cannot be edited. Undefined when both are live,
 * so the status tooltip stays in charge.
 */
export function bookingDeletionEditReason(booking: BookingDeletionSource): string | undefined {
  if (booking.address?.isDeleted)
    return i18n.global.t('This booking cannot be edited because its address was deleted.')

  if (booking.gate?.isDeleted)
    return i18n.global.t('This booking cannot be edited because its gate was deleted.')

  return undefined
}

/**
 * Booking slot label on the address clock: "Today, 10:00 - 10:30" for today *at the address*,
 * otherwise "12.12.2027, 10:00 - 10:30". A blank zone renders on the reader's clock.
 */
export function formatBookingTime(
  timeFrom: string | null | undefined,
  timeTo: string | null | undefined,
  timeZone?: string,
): string {
  const from = stringToDate(timeFrom)
  if (!from)
    return ''

  const dayLabel = formatRelativeDayLabelInZone(from, timeZone)
  const fromTime = formatDateInZone(from, timeZone, 'HH:mm')
  const to = stringToDate(timeTo)
  if (!to)
    return `${dayLabel}, ${fromTime}`

  const sameDay = formatDateInZone(from, timeZone, ISO_DATE_FORMAT) === formatDateInZone(to, timeZone, ISO_DATE_FORMAT)
  const toTimeFormatted = formatDateInZone(to, timeZone, sameDay ? 'HH:mm' : 'dd.MM.yyyy HH:mm')

  return `${dayLabel}, ${fromTime} - ${toTimeFormatted}`
}

/** Split a booking start instant into the date and time fields the slot form posts. */
export function bookingSlotDateTime(
  startAt: string,
  timeZone: string,
): { date: string; time: string } {
  return {
    date: formatDateStringInZone(startAt, timeZone, ISO_DATE_FORMAT),
    time: formatDateStringInZone(startAt, timeZone, 'HH:mm:ss'),
  }
}

/** The slot start on the address clock, e.g. "21. 11. 2027 - 14:30". */
export function formatBookingSlotStart(startAt: string, timeZone: string): string {
  return formatDateStringInZone(startAt, timeZone, BOOKING_SLOT_START_FORMAT)
}

export function bookingDetailTo(id: number, action?: RouteAction): RouteLocationNamedRaw {
  return {
    name: 'bookings-view-id',
    params: { id },
    ...(action ? { query: { action } } : {}),
  }
}

export function bookingEditTo(id: number): RouteLocationNamedRaw {
  return { name: 'bookings-edit-id', params: { id } }
}

// Canceled, rejected and withdrawn bookings can be replaced. barAction applies those rebooking rules.
const REVOKED_BOOKING_STATUSES = new Set<TimeSlotBookingListStatusEnum>([
  TimeSlotBookingListStatusEnum.CANCELED,
  TimeSlotBookingListStatusEnum.REJECTED,
  TimeSlotBookingListStatusEnum.WITHDRAWN,
])

type BookingTimelineSource = Pick<TimeSlotBookingList, 'id' | 'startAt' | 'timeZone' | 'status'> & {
  address: Pick<TimeSlotBookingList['address'], 'id' | 'isDeleted'>
  gate: Pick<GateNested, 'id'>
}

/** False on a deleted address: the timeline does not list it, so there is nothing to focus. */
export function canShowBookingOnTimeline(booking: BookingTimelineSource): boolean {
  return !booking.address.isDeleted
}

/** The booking's day on its address clock, focused on the booking, with revoked bookings shown when it is one. */
export function bookingTimelineTo(booking: BookingTimelineSource): RouteLocationNamedRaw {
  const focus = canShowBookingOnTimeline(booking)
    ? { addressId: booking.address.id, gateId: booking.gate.id, bookingId: booking.id }
    : undefined

  return bookingTimelineLocation(
    bookingSlotDateTime(booking.startAt, booking.timeZone).date,
    focus,
    REVOKED_BOOKING_STATUSES.has(booking.status),
  )
}

// Expired is not a stored status - it is a pending booking whose arrival time passed, which is why
// it can still be rejected while approving it is already too late.
const APPROVABLE_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.EXPIRED,
]

const REJECTABLE_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.EXPIRED,
]

const EDITABLE_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.APPROVED,
]

// The shipper rejects pending/expired bookings instead of cancelling them.
const SHIPPER_CANCELABLE_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.APPROVED,
]

export function canApproveBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return APPROVABLE_STATUSES.includes(status)
}

export function canRejectBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return REJECTABLE_STATUSES.includes(status)
}

export function canApproveTransportBooking(
  transportStatus: TransportStatusEnum | null | undefined,
  bookingStatus: TimeSlotBookingListStatusEnum | null | undefined,
): boolean {
  return isTransportBookingActionable(transportStatus)
    && !isNullOrUndefined(bookingStatus)
    && canApproveBooking(bookingStatus)
}

export function canRejectTransportBooking(
  transportStatus: TransportStatusEnum | null | undefined,
  bookingStatus: TimeSlotBookingListStatusEnum | null | undefined,
): boolean {
  return isTransportBookingActionable(transportStatus)
    && !isNullOrUndefined(bookingStatus)
    && bookingStatus !== TimeSlotBookingListStatusEnum.PENDING
    && canRejectBooking(bookingStatus)
}

export function canEditBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return EDITABLE_STATUSES.includes(status)
}

export function canCarrierEditBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return status === TimeSlotBookingListStatusEnum.PENDING
}

export function canShipperCancelBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return SHIPPER_CANCELABLE_STATUSES.includes(status)
}

// Same rule as backend validate_booking_editable: a status with no outgoing transition is
// settled. EXPIRED is stored PENDING, so it stays allowed. ARRIVED can still be re-docked.
const GATE_CHANGEABLE_STATUSES: TimeSlotBookingListStatusEnum[] = [
  TimeSlotBookingListStatusEnum.PENDING,
  TimeSlotBookingListStatusEnum.APPROVED,
  TimeSlotBookingListStatusEnum.EXPIRED,
  TimeSlotBookingListStatusEnum.ARRIVED,
]

export function canChangeGateBooking(status: TimeSlotBookingListStatusEnum): boolean {
  return GATE_CHANGEABLE_STATUSES.includes(status)
}

/** Change gate needs a live address. A missing or deleted address has no dock to move to. */
export function bookingAddressAllowsChangeGate(address: { isDeleted: boolean } | null | undefined): boolean {
  return address?.isDeleted === false
}

// The status cancel writes - same edge as time_slot_booking_cancel. The endpoint is 204, so
// the success toast has to name this, not the verb on the button.
export function getBookingCancelResultStatus(status: TimeSlotBookingListStatusEnum): TimeSlotBookingListStatusEnum {
  return status === TimeSlotBookingListStatusEnum.APPROVED
    ? TimeSlotBookingListStatusEnum.CANCELED
    : TimeSlotBookingListStatusEnum.WITHDRAWN
}

// The carrier's cancel on a request the shipper has not approved yet withdraws it, so it is offered as Withdraw.
export function isBookingWithdrawable(status: TimeSlotBookingListStatusEnum): boolean {
  return canCancelBooking(status) && getBookingCancelResultStatus(status) === TimeSlotBookingListStatusEnum.WITHDRAWN
}

export function getBookingCancelSuccessMessage(status: TimeSlotBookingListStatusEnum): string {
  return isBookingWithdrawable(status)
    ? i18n.global.t('Booking has been withdrawn')
    : i18n.global.t('Booking has been canceled')
}

export function resolveBookingActionErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof AxiosError) || isNullOrUndefined(error.response) || error.response.status !== HttpStatusCode.BadRequest)
    return fallback

  return firstApiErrorMessage(error.response.data) ?? fallback
}

// Only a 400 explains itself ("Approved booking cannot be changed to Rejected."); every other
// status answers in DRF's own wording, which names internals rather than causes.
export function resolveBookingRejectErrorMessage(error: unknown, fallback: string): string {
  return resolveBookingActionErrorMessage(error, fallback)
}

// A slot taken meanwhile (AC9) answers 400 under `gate`.
export function resolveBookingApproveErrorMessage(error: unknown, fallback: string): string {
  return resolveBookingActionErrorMessage(error, fallback)
}

// Same 400-only rule as reject: the transition refusal is a ValidationError, and a 403 is a dead OTP
// session (or a blocked carrier), not a booking message.
export function resolveBookingCancelErrorMessage(error: unknown, fallback: string): string {
  return resolveBookingActionErrorMessage(error, fallback)
}

export function resolveBookingChangeGateErrorMessage(error: unknown, fallback: string): string {
  return resolveBookingActionErrorMessage(error, fallback)
}

const CARRIER_BOOKING_NOT_EDITABLE_ERROR_CODE = 'booking_not_editable'

/** The reason a carrier edit was refused as no longer editable, else null. */
export function carrierBookingNotEditableReason(error: unknown): string | null {
  if (!isBadRequestError(error))
    return null

  const data = error.response?.data as { code?: string; message?: string } | undefined

  return data?.code === CARRIER_BOOKING_NOT_EDITABLE_ERROR_CODE ? data.message ?? null : null
}

// Also matches the rare no-bookable-gate and write-failure refusals, which share the slot banner by design.
export function resolveBookingSlotRejectionMessage(error: unknown): string | null {
  if (!isBadRequestError(error))
    return null

  const { nonFieldErrors, ...fieldErrors } = (error.response?.data ?? {}) as Record<string, unknown>

  return Object.keys(fieldErrors).length === 0 ? firstApiErrorMessage({ nonFieldErrors }) ?? null : null
}

export function bookingGateSelectOptions(
  gates: Pick<GateNested, 'id' | 'name'>[],
): { value: number; label: string }[] {
  return gates.map(gate => ({ value: gate.id, label: gate.name }))
}

/** Keep the user's pick when it is still offered; otherwise the current gate if still offered. */
export function resolveBookingChangeGateSelection(
  gates: Pick<GateNested, 'id'>[],
  currentGateId: number,
  selectedGateId: number | undefined,
): number | undefined {
  if (!isNullOrUndefined(selectedGateId) && gates.some(gate => gate.id === selectedGateId))
    return selectedGateId

  if (gates.some(gate => gate.id === currentGateId))
    return currentGateId

  return undefined
}

export function formatBookingCreatedAt(createdAt: string | null | undefined): string {
  return formatDateString(createdAt, BOOKING_CREATED_AT_FORMAT)
}

type BookingTransportLabelSource = Pick<
  BookingTransportDetailNested | CarrierBookingTransportDetailNested | BookingTransportPreviewNested,
  'displayId' | 'waypoints'
>

/** Transport label on a booking detail: "#23451 Prague → Paris → Berlin". */
export function formatBookingTransportLabel(
  transport: BookingTransportLabelSource | null | undefined,
): string {
  if (isNullOrUndefined(transport))
    return ''

  const cities = transport.waypoints.map(waypoint => waypoint.city).filter(Boolean).join(' → ')

  return [transport.displayId, cities].filter(Boolean).join(' ')
}

export function formatBookingPlates(vehicles: BookingVehicleNested[] | undefined): string {
  return formatBookingDash(
    (vehicles ?? [])
      .map(vehicle => vehicle.plateNumber)
      .filter(Boolean)
      .join(', '),
  )
}

export function isWaypointWithBooking(waypoint: WaypointOrWaypointByHash): waypoint is WaypointWithBooking {
  return 'booking' in waypoint
}

// Decided by the backend from the stored status, so the detail agrees with the list badge.
export function isWaypointBookingRequired(waypoint: Pick<WaypointWithBooking, 'bookingRequired'>): boolean {
  return waypoint.bookingRequired
}

export enum WaypointBookingAction {
  BookSlot = 'bookSlot',
  ShowDetails = 'showDetails',
}

export interface WaypointBookingBarState {
  label: string
  action: WaypointBookingAction | null
  actionDisabledReason: string | null

  /** Rendered after the label by the bar, so a deleted gate can be struck through on its own. */
  gate: Pick<GateNested, 'name' | 'isDeleted'> | null

  /** Muted bar: the transport was canceled before this stop had a booking. */
  unnecessary: boolean
}

export interface WaypointBookingBarContext {
  transportStatus?: TransportStatusEnum | null
}

type WaypointBookingBarConfig = {
  action: WaypointBookingAction | null
  label: (slot: string) => string
  showsSlot: boolean
}

// One wording for both audiences: the carrier and shipper transport-detail designs read the same bar.
// Bar colour comes from the booking status badge, so this map only decides the label and the action.
const WAYPOINT_BOOKING_BARS: Record<TimeSlotBookingListStatusEnum, WaypointBookingBarConfig> = {
  [TimeSlotBookingListStatusEnum.PENDING]: { action: WaypointBookingAction.ShowDetails, showsSlot: true, label: slot => i18n.global.t('Pending approval: {slot}', { slot }) },
  [TimeSlotBookingListStatusEnum.APPROVED]: { action: WaypointBookingAction.ShowDetails, showsSlot: true, label: slot => i18n.global.t('Booking approved: {slot}', { slot }) },
  [TimeSlotBookingListStatusEnum.CANCELED]: { action: WaypointBookingAction.BookSlot, showsSlot: true, label: slot => i18n.global.t('Booking canceled: {slot}', { slot }) },
  [TimeSlotBookingListStatusEnum.REJECTED]: { action: WaypointBookingAction.BookSlot, showsSlot: true, label: slot => i18n.global.t('Booking rejected: {slot}', { slot }) },
  [TimeSlotBookingListStatusEnum.WITHDRAWN]: { action: WaypointBookingAction.BookSlot, showsSlot: true, label: slot => i18n.global.t('Booking withdrawn: {slot}', { slot }) },
  [TimeSlotBookingListStatusEnum.EXPIRED]: { action: WaypointBookingAction.ShowDetails, showsSlot: false, label: () => i18n.global.t('Booking expired') },
  [TimeSlotBookingListStatusEnum.ARRIVED]: { action: null, showsSlot: false, label: () => i18n.global.t('Booking status: Arrived') },
  [TimeSlotBookingListStatusEnum.COMPLETED]: { action: null, showsSlot: false, label: () => i18n.global.t('Booking status: Completed') },
}

function barState(
  action: WaypointBookingAction | null,
  label: string,
  actionDisabledReason: string | null = null,
  gate: WaypointBookingBarState['gate'] = null,
  unnecessary = false,
): WaypointBookingBarState {
  return { action, label, actionDisabledReason, gate, unnecessary }
}

function bookingRequiredBar(): WaypointBookingBarState {
  return barState(WaypointBookingAction.BookSlot, i18n.global.t('Booking required'))
}

function bookingUnnecessaryBar(): WaypointBookingBarState {
  return barState(null, transportCanceledBookingExplanation(), null, null, true)
}

function bookingRequiredBarWhileOpen(bookingRequired: boolean, transportStatus: TransportStatusEnum | null | undefined): WaypointBookingBarState | null {
  return bookingRequired && isTransportBookingAllowed(transportStatus) ? bookingRequiredBar() : null
}

function barAction(
  status: TimeSlotBookingListStatusEnum,
  config: WaypointBookingBarConfig,
  bookingRequired: boolean,
  transportStatus: TransportStatusEnum | null | undefined,
): Pick<WaypointBookingBarState, 'action' | 'actionDisabledReason'> {
  const revoked = REVOKED_BOOKING_STATUSES.has(status)

  if (isTransportStatusCanceled(transportStatus) && revoked) {
    return {
      action: WaypointBookingAction.BookSlot,
      actionDisabledReason: transportCanceledBookingExplanation(),
    }
  }

  const action = revoked && !bookingRequired ? null : config.action
  const slotClosed = transportStatus === TransportStatusEnum.FINISHED && action === WaypointBookingAction.BookSlot

  return {
    action: slotClosed ? null : action,
    actionDisabledReason: null,
  }
}

export function getWaypointBookingBar(
  waypoint: Pick<WaypointWithBooking, 'booking' | 'bookingRequired'>,
  context: WaypointBookingBarContext = {},
): WaypointBookingBarState | null {
  const { booking, bookingRequired } = waypoint
  const { transportStatus } = context

  if (isNullOrUndefined(booking)) {
    return isTransportStatusCanceled(transportStatus)
      ? bookingUnnecessaryBar()
      : bookingRequiredBarWhileOpen(bookingRequired, transportStatus)
  }

  // A booking status this client does not know degrades to "booking required", the backend's verdict
  // for a status outside its covering set.
  const config: WaypointBookingBarConfig | undefined = WAYPOINT_BOOKING_BARS[booking.status]
  if (isNullOrUndefined(config))
    return bookingRequiredBarWhileOpen(bookingRequired, transportStatus)

  const { action, actionDisabledReason } = barAction(booking.status, config, bookingRequired, transportStatus)
  const gate = config.showsSlot && booking.gate.name ? booking.gate : null

  return barState(
    action,
    config.label(formatBookingTime(booking.startAt, booking.endAt, booking.timeZone)),
    actionDisabledReason,
    gate,
  )
}

// Gated on time slots like the bar (WaypointBookingBar), so the banner never points at a waypoint without one:
// disabling slots on an address leaves its pending bookings in place.
export function isWaypointBookingApprovable(waypoint: WaypointOrWaypointByHash): boolean {
  return isWaypointWithBooking(waypoint)
    && waypoint.timeSlotsEnabled
    && !isNullOrUndefined(waypoint.booking)
    && canApproveBooking(waypoint.booking.status)
}
